import { useEffect, useRef, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { api, ALLOWED_REACTIONS } from "@/lib/api";
import { Send, Loader2, MoreVertical, Flag, Trash2, Lock, Crown, Skull } from "lucide-react";
import { RoleBadge } from "@/components/RoleBadge";
import { RankBadge } from "@/components/RankBadge";
import { StreakBadge } from "@/components/StreakBadge";
import { BannerDisplay } from "@/components/Banner";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { playPopSound } from "@/lib/sound";
import { toast } from "sonner";

function relativeTime(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const diff = Math.floor((Date.now() - t) / 1000);
    if (diff < 5) return "just now";
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
}

const MessageBubble = ({ m, currentUser, onReact, onDelete, onReport }) => {
    const reactions = m.reactions || {};
    const canDelete = currentUser && (currentUser.role === "admin" || currentUser.role === "support");
    const isOwn = currentUser && m.author_id === currentUser.id;
    const accent = m.author_role === "admin" ? "#facc15"
        : m.author_role === "support" ? "#22d3ee"
        : m.author_avatar_color || "#a855f7";
    const isStaff = m.author_role === "admin" || m.author_role === "support";

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="flex gap-3 group"
            data-testid={`lounge-message-${m.id}`}
        >
            <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs text-white flex-shrink-0" style={{ background: accent }}>
                {(m.author_name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <Link to={`/u/${m.author_id}`} className="font-semibold text-white hover:underline truncate">{m.author_name}</Link>
                    <RoleBadge role={m.author_role} />
                    {m.author_rank && <RankBadge rank={{ key: m.author_rank, streak: m.author_streak || 0 }} size="sm" showLabel={false} />}
                    {!isStaff && m.author_streak ? <StreakBadge streak={m.author_streak} size="sm" /> : null}
                    <span className="text-[10px] text-[#5e5e72] font-mono">{relativeTime(m.created_at)}</span>
                </div>
                {m.author_equipped_banner && (
                    <div className="mt-1 max-w-[300px]">
                        <BannerDisplay banner={m.author_equipped_banner} height={32} />
                    </div>
                )}
                <p className="mt-1 text-sm text-white whitespace-pre-wrap break-words">{m.content}</p>
                <div className="mt-1 flex items-center gap-1 flex-wrap">
                    {ALLOWED_REACTIONS.map((emoji) => {
                        const arr = reactions[emoji] || [];
                        const mine = currentUser && arr.includes(currentUser.id);
                        if (arr.length === 0 && !mine) return null;
                        return (
                            <button
                                key={emoji}
                                onClick={() => onReact(m.id, emoji)}
                                className={`px-1.5 py-0.5 rounded text-xs border ${mine ? "border-[#a855f7] bg-[#1a0f2e]" : "border-[#242436] hover:border-[#a855f7]"}`}
                                data-testid={`lounge-react-${emoji}-${m.id}`}
                            >
                                {emoji} {arr.length}
                            </button>
                        );
                    })}
                    {currentUser && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="text-[10px] text-[#5e5e72] hover:text-[#a855f7] font-mono uppercase opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`lounge-react-menu-${m.id}`}>
                                    + react
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="pp-bg-1 border-[#242436] text-white">
                                {ALLOWED_REACTIONS.map(e => (
                                    <DropdownMenuItem key={e} onClick={() => onReact(m.id, e)} className="text-base hover:bg-[#1b1b26]">{e}</DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button className="p-1 rounded hover:bg-[#1b1b26] text-[#8b8ba0] hover:text-white" data-testid={`lounge-message-menu-${m.id}`}>
                            <MoreVertical className="w-4 h-4" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="pp-bg-1 border-[#242436] text-white">
                        {!isOwn && (
                            <DropdownMenuItem onClick={() => onReport(m)} className="text-[#ef4444] hover:bg-[#1a0606] hover:text-[#ef4444]" data-testid={`lounge-report-${m.id}`}>
                                <Flag className="w-3.5 h-3.5 mr-2" /> Report
                            </DropdownMenuItem>
                        )}
                        {canDelete && (
                            <DropdownMenuItem onClick={() => onDelete(m.id)} className="text-[#ef4444] hover:bg-[#1a0606] hover:text-[#ef4444]" data-testid={`lounge-delete-${m.id}`}>
                                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                            </DropdownMenuItem>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </motion.div>
    );
};

export const LoungeChat = ({ channel }) => {
    const { user, loading: authLoading } = useAuth();
    const [messages, setMessages] = useState([]);
    const [content, setContent] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [accessError, setAccessError] = useState(null);
    const [reportTarget, setReportTarget] = useState(null);
    const [reportReason, setReportReason] = useState("");
    const [submittingReport, setSubmittingReport] = useState(false);
    const scrollRef = useRef(null);
    const lastIsoRef = useRef(null);

    const channelInfo = channel === "pro"
        ? { label: "PRO LOUNGE", color: "#a855f7", icon: Crown, requirement: "Pro tier required" }
        : { label: "PREMIUM LOUNGE", color: "#ef4444", icon: Skull, requirement: "Sports Premium required" };

    const fetchMessages = useCallback(async (full = false) => {
        try {
            const params = {};
            if (!full && lastIsoRef.current) params.since = lastIsoRef.current;
            const res = await api.get(`/chat/lounge/${channel}/messages`, { params });
            const items = res.data.items || [];
            if (full) {
                setMessages(items);
            } else if (items.length) {
                setMessages((cur) => {
                    const ids = new Set(cur.map((m) => m.id));
                    return [...cur, ...items.filter(i => !ids.has(i.id))];
                });
            }
            if (items.length) lastIsoRef.current = items[items.length - 1].created_at;
            setAccessError(null);
            requestAnimationFrame(() => {
                if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            });
        } catch (e) {
            if (e?.response?.status === 403) {
                setAccessError(e?.response?.data?.detail || channelInfo.requirement);
            }
        } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel]);

    useEffect(() => {
        lastIsoRef.current = null;
        setMessages([]);
        setLoading(true);
        if (user) fetchMessages(true);
        else setLoading(false);
    }, [channel, user, fetchMessages]);

    useEffect(() => {
        if (!user || accessError) return;
        const interval = setInterval(() => fetchMessages(false), 8000);
        return () => clearInterval(interval);
    }, [user, accessError, fetchMessages]);

    const send = async (e) => {
        e?.preventDefault();
        const c = content.trim();
        if (!c) return;
        setSending(true);
        try {
            await api.post(`/chat/lounge/${channel}/messages`, { content: c });
            setContent("");
            playPopSound();
            await fetchMessages(false);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 403) toast.error(detail || "You can't post here");
            else toast.error(detail || "Failed to send");
        } finally { setSending(false); }
    };

    const reactTo = async (id, emoji) => {
        if (!user) return toast.message("Log in to react");
        try {
            await api.post(`/chat/lounge/messages/${id}/react`, { emoji });
            await fetchMessages(true);
        } catch (e) { /* noop */ }
    };

    const deleteMsg = async (id) => {
        if (!window.confirm("Delete this message?")) return;
        try {
            await api.delete(`/chat/lounge/messages/${id}`);
            setMessages((cur) => cur.filter(m => m.id !== id));
            toast.success("Message deleted");
        } catch (e) { toast.error("Failed to delete"); }
    };

    const submitReport = async () => {
        if (!reportTarget || reportReason.trim().length < 3) { toast.error("Tell us why (min 3 chars)"); return; }
        setSubmittingReport(true);
        try {
            await api.post("/chat/reports", {
                target_type: "message",
                target_id: reportTarget.id,
                reason: reportReason.trim(),
            });
            toast.success("Thanks — the team will review this report.");
            setReportTarget(null);
            setReportReason("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't send report");
        } finally { setSubmittingReport(false); }
    };

    const Icon = channelInfo.icon;

    return (
        <Card className="pp-bg-1 border-[#242436] text-white" data-testid={`lounge-chat-${channel}`}>
            <CardContent className="p-0 flex flex-col h-[60vh] sm:h-[68vh]">
                <div className="px-4 py-3 border-b border-[#242436] flex items-center gap-2" style={{ borderBottomColor: `${channelInfo.color}33` }}>
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-md" style={{ background: `${channelInfo.color}22`, border: `1px solid ${channelInfo.color}66` }}>
                        <Icon className="w-4 h-4" style={{ color: channelInfo.color }} />
                    </span>
                    <div>
                        <p className="font-display tracking-widest" style={{ color: channelInfo.color }}>{channelInfo.label}</p>
                        <p className="text-[10px] font-mono uppercase text-[#8b8ba0]">show off your wins, streaks & banners. don't spam.</p>
                    </div>
                </div>

                {accessError ? (
                    <div className="flex-1 flex items-center justify-center p-8">
                        <div className="text-center max-w-sm">
                            <Lock className="w-10 h-10 mx-auto mb-3" style={{ color: channelInfo.color }} />
                            <p className="font-display text-2xl tracking-widest mb-2" data-testid={`lounge-locked-${channel}`}>LOUNGE LOCKED</p>
                            <p className="text-sm text-[#b7b7c6] mb-4">{accessError}</p>
                            <Button asChild className="text-white font-semibold" style={{ background: channelInfo.color }}>
                                <Link to="/pricing">Subscribe to unlock</Link>
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid={`lounge-messages-${channel}`}>
                            {loading ? (
                                <div className="flex items-center justify-center py-12 text-[#8b8ba0]"><Loader2 className="w-5 h-5 animate-spin" /></div>
                            ) : messages.length === 0 ? (
                                <p className="text-center text-[#8b8ba0] text-sm py-12">Crickets... be the first to say something 🎯</p>
                            ) : (
                                <AnimatePresence>
                                    {messages.map((m) => (
                                        <MessageBubble
                                            key={m.id}
                                            m={m}
                                            currentUser={user}
                                            onReact={reactTo}
                                            onDelete={deleteMsg}
                                            onReport={(msg) => { setReportTarget(msg); setReportReason(""); }}
                                        />
                                    ))}
                                </AnimatePresence>
                            )}
                        </div>
                        <div className="border-t border-[#242436] p-3">
                            {authLoading ? (
                                <div className="text-center text-sm text-[#8b8ba0] py-2 inline-flex items-center justify-center gap-2 w-full">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                                </div>
                            ) : !user ? (
                                <div className="text-center text-sm text-[#8b8ba0] py-2">
                                    <Link to="/login" className="text-[#22d3ee] hover:underline">Log in</Link> to chat
                                </div>
                            ) : (
                                <form onSubmit={send} className="flex items-center gap-2" data-testid={`lounge-compose-${channel}`}>
                                    <Input
                                        value={content}
                                        onChange={(e) => setContent(e.target.value.slice(0, 500))}
                                        placeholder="say something..."
                                        className="pp-bg-0 border-[#242436] text-white"
                                        data-testid={`lounge-input-${channel}`}
                                        maxLength={500}
                                    />
                                    <Button type="submit" disabled={sending || !content.trim()} className="text-white font-semibold" style={{ background: channelInfo.color }} data-testid={`lounge-send-${channel}`}>
                                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    </Button>
                                </form>
                            )}
                        </div>
                    </>
                )}
            </CardContent>

            {/* Report dialog */}
            <Dialog open={!!reportTarget} onOpenChange={(open) => { if (!open) { setReportTarget(null); setReportReason(""); } }}>
                <DialogContent className="pp-bg-1 border-[#242436] text-white" data-testid="lounge-report-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white inline-flex items-center gap-2"><Flag className="w-5 h-5 text-[#ef4444]" /> Report this message</DialogTitle>
                        <DialogDescription className="text-[#b7b7c6]">
                            The host and support team will review this. Tell them what's wrong.
                        </DialogDescription>
                    </DialogHeader>
                    {reportTarget && (
                        <div className="pp-bg-0 rounded-md p-3 border border-[#242436] text-sm">
                            <p className="text-xs text-[#8b8ba0] font-mono uppercase">{reportTarget.author_name}:</p>
                            <p className="mt-1 text-white">"{reportTarget.content}"</p>
                        </div>
                    )}
                    <Textarea
                        value={reportReason}
                        onChange={(e) => setReportReason(e.target.value.slice(0, 500))}
                        placeholder="Why are you reporting this? (e.g. spam, harassment, profanity, threats)"
                        rows={3}
                        className="pp-bg-0 border-[#242436] text-white"
                        data-testid="lounge-report-reason"
                    />
                    <p className="text-[10px] font-mono text-[#8b8ba0]">{reportReason.length}/500</p>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setReportTarget(null)} className="bg-[#14141c] border border-[#242436] text-white hover:bg-[#1b1b26]">Cancel</Button>
                        <Button onClick={submitReport} disabled={submittingReport} className="bg-[#ef4444] hover:brightness-110 text-white font-semibold" data-testid="lounge-report-submit">
                            {submittingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="inline-flex items-center gap-1"><Flag className="w-4 h-4" /> Send report</span>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
};
